import { memo, useMemo } from "react";
import { VehicleListing, VehicleListingChannel, MarketplaceDetails } from "services/api/sales/listings/types";
import { MARKETPLACE_CHANNEL_CODE, initialChannelDataEntry } from "../../consts";
import { buildMarketplacePayload } from "../../mapping/buildPayload";
import ListingGalleryPreview from "../../../../ListingDetailPage/components/ListingGalleryPreview/ListingGalleryPreview";
import ListingSpecification from "../../../../ListingDetailPage/components/ListingSpecification/ListingSpecification";
import { PreviewStepProps } from "./types";

// Završni wizard korak. Preview je 1:1 izgledom s detail page-om — koristi
// iste komponente (`ListingGalleryPreview` + `ListingSpecification`), samo s
// syntetskim `VehicleListing` / `VehicleListingChannel` konstruiranim iz
// `formValues` (jer listing još ne postoji u BE-u kad se preview prikazuje
// tijekom kreiranja).
const PreviewStep: React.FC<PreviewStepProps> = (props) => {
  const { formValues } = props;
  const { vehicle, pictures } = formValues;

  // Channels: VehicleListingChannel synth iz odabranih SalesChannel-ova.
  // Slike kanala dodjeljujemo samo marketplace-u; ostali kanali u previewu
  // ionako nasljeđuju marketplace baseline.
  const syntheticChannels: VehicleListingChannel[] = useMemo(() => {
    const basePrice = formValues.basePrice ? Number(formValues.basePrice) : null;
    return formValues.channels
      .filter((c) => formValues.selectedChannelIds.includes(c.id))
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((c) => {
        const cd = formValues.channelData[c.id] ?? initialChannelDataEntry;
        const priceOverride = cd.priceOverride ? Number(cd.priceOverride) : null;
        const isMarketplace = c.code === MARKETPLACE_CHANNEL_CODE;
        return {
          id: c.id,
          salesChannelId: c.id,
          salesChannelName: c.name,
          salesChannelCode: c.code,
          isActive: true,
          priceOverride,
          titleOverride: cd.titleOverride || null,
          descriptionOverride: cd.descriptionOverride || null,
          effectivePrice: priceOverride ?? basePrice,
          effectiveTitle: cd.titleOverride || formValues.baseTitle || null,
          effectiveDescription: cd.descriptionOverride || formValues.baseDescription || null,
          externalListingId: null,
          externalListingUrl: null,
          lastSynced: null,
          syncStatus: null,
          syncError: null,
          images: isMarketplace
            ? formValues.selectedPictureUploadIds.map((uid, i) => ({
                id: uid,
                uploadId: uid,
                sortOrder: i,
              }))
            : [],
          njuskaloDetails: null,
        };
      });
  }, [formValues]);

  const marketplaceChannel = useMemo(
    () =>
      syntheticChannels.find((c) => c.salesChannelCode === MARKETPLACE_CHANNEL_CODE)
      ?? syntheticChannels[0],
    [syntheticChannels]
  );

  // marketplaceDetails: flatten form state u BE-shape preko postojećeg
  // buildMarketplacePayload helpera; dodaje se samo `id: null`.
  const syntheticDetails: MarketplaceDetails = useMemo(
    () => ({ id: null, ...(buildMarketplacePayload(formValues) as Omit<MarketplaceDetails, "id">) }),
    [formValues]
  );

  // fleetVehicleName se sastavlja iz manufacturer + model (FleetVehicle ima
  // ravne `vehicleManufacturer` / `vehicleModel` propertije, ne nested chain).
  const fleetVehicleName = useMemo(() => {
    const manufacturer = vehicle?.vehicleManufacturer?.name ?? "";
    const model = vehicle?.vehicleModel?.name ?? "";
    return `${manufacturer} ${model}`.trim();
  }, [vehicle]);

  const syntheticListing: VehicleListing = useMemo(
    () => ({
      id: "preview",
      fleetVehicleId: vehicle?.id ?? null,
      fleetVehicleName: fleetVehicleName || null,
      status: "draft",
      basePrice: formValues.basePrice ? Number(formValues.basePrice) : null,
      baseTitle: formValues.baseTitle || null,
      baseDescription: formValues.baseDescription || null,
      published: null,
      created: null,
      channels: syntheticChannels,
      fleetVehiclePictures: pictures.map((p) => ({
        id: p.id,
        url: p.url,
        name: p.name ?? "",
      })),
      vehicle,
      location: null,
      marketplaceDetails: syntheticDetails,
      // Kontakt i prodavatelj dolaze s backenda i vide se tek na spremljenom oglasu.
      contacts: [],
      offices: [],
      company: null,
    }),
    [formValues.basePrice, formValues.baseTitle, formValues.baseDescription, vehicle, fleetVehicleName, syntheticChannels, pictures, syntheticDetails]
  );

  if (!marketplaceChannel) {
    return (
      <div className="w-full max-w-3xl mx-auto px-6 py-12 text-center">
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Pregled oglasa</h2>
        <p className="text-sm text-slate-500 max-w-md mx-auto">
          Odaberite barem jedan oglasnik na prvom koraku da bi se preview prikazao.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto px-6 py-8">
      <div className="flex flex-col items-center text-center mb-6">
        <h2 className="text-2xl font-bold text-slate-800">Pregled oglasa</h2>
        <p className="text-sm text-slate-500 mt-2 max-w-md">
          Konačni izgled oglasa s unesenim podacima. Provjerite naslov, cijenu, opis i odabrane slike prije objave.
        </p>
      </div>

      <div className="bg-white rounded-sm border">
        <ListingGalleryPreview
          allPictures={syntheticListing.fleetVehiclePictures}
          channel={marketplaceChannel}
          marketplaceChannel={marketplaceChannel}
        />
        <ListingSpecification
          listing={syntheticListing}
          channel={marketplaceChannel}
          vehicle={vehicle}
        />
      </div>
    </div>
  );
};

export default memo(PreviewStep);
